import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Icon from 'assets/components/icon/Icon';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import Page from 'assets/components/page/Page';
import ThemePageStatic1 from 'assets/components/page/themes/Theme.Page.Static.1';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { useState } from 'react';
import { productIndexRoute } from '../../product/Product.Index';
import { productEnhancementIndexRoute } from '../../productEnhancement/ProductEnhancement.Index';
import { usePriceEditor } from '../hooks/usePriceEditor';
import { usePriceSelectionTools } from '../hooks/usePriceSelectionTools';
import PriceAdjustmentAddFilter from './PriceAdjustmentAddFilter';
import PriceAdjustmentSelectionTools from './PriceAdjustmentSelectionTools';
import PriceAdjustmentSummary from './PriceAdjustmentSummary';
import PriceEditTable from './PriceEditTable';
import PriceExport from './PriceExport';
import PriceImport from './PriceImport';
import ProductType from 'models/enums/ProductType';

type PriceAdjustmentsPageProps = {
  isLoading?: boolean;
  title?: Utils.ReactNodes;
  id?: number;
  dataType?: Module.ProductManagement.PriceAdjustments.DataType;
};
export default function PriceAdjustmentsPage({ isLoading, title, id, dataType }: PriceAdjustmentsPageProps) {
  const { lang } = getLocales();
  const { permissions } = useAuthStore();
  const { pricingType, hasNoInfo, isSaving, isUpdated, prices, allPrices, saveAll, onSuccessImport } = usePriceEditor();
  const priceSelectionTools = usePriceSelectionTools();
  const isInTransition = isSaving || isLoading;
  const [isExporting, setIsExporting] = useState<boolean>(false);

  return (
    <Page
      class={ThemePageStatic1}
      style={{
        pageContent: isInTransition || hasNoInfo ? undefined : { padding: 0 },
        pageSubHeader: {
          margin: 0,
          padding: `${Theme.Size.L} ${Theme.Size.L} ${Theme.Size.M} ${Theme.Size.L}`,
          height: 'fit-content',
          zIndex: 9,
        },
        pageHeader: {
          backgroundColor: Theme.Color.backgroundDark,
          color: Theme.Color.primaryDark,
          zIndex: 10,
        },
      }}
      header={
        isLoading ? undefined : (
          <>
            <Card
              class={ThemeCardRow}
              info={title}
              title={
                <>
                  {lang[pricingType]} {lang.adjustments}
                </>
              }
              subtitle={
                isLoading ? undefined : (
                  <>
                    {lang.showing} {prices.length}/{allPrices.length} {lang.prices}
                  </>
                )
              }
              actions={!isInTransition && <PriceAdjustmentSelectionTools />}
            />
          </>
        )
      }
      subHeader={!isInTransition && <PriceAdjustmentAddFilter />}
      render={
        isSaving ? (
          <ErrorWrapper>{lang.savingPleaseWait}</ErrorWrapper>
        ) : isLoading ? (
          <ErrorWrapper>{lang.loadingPleaseWait}</ErrorWrapper>
        ) : hasNoInfo ? (
          <ErrorWrapper>{lang.noInfo}</ErrorWrapper>
        ) : (
          <>
            <PriceEditTable
              dealerId={priceSelectionTools.dealer?.id}
              distributorId={priceSelectionTools.distributor?.id}
            />
            {isExporting &&
              (pricingType === ProductType.warrantiClassBased || pricingType === ProductType.warrantyTierBased) && (
                <PriceExport productId={id} setIsExporting={setIsExporting} />
              )}
          </>
        )
      }
      footer={
        <>
          <PriceAdjustmentSummary />
          <Button
            onClick={() =>
              document.location.replace(
                createPathFromRoute(dataType === 'product' ? productIndexRoute : productEnhancementIndexRoute, {
                  action: 'edit',
                  id,
                })
              )
            }
            disabled={!permissions.EDIT_PRODUCT}
            class={ThemeButtonCircle}
            media={<Icon class="fas-edit" />}
            style={{ marginLeft: 'auto' }}
            render={dataType === 'product' ? lang.goToProductEdit : lang.goToEnhancementEdit}
            htmlElementProps={{ title: lang.edit }}
          />
          <Button
            onClick={setIsExporting}
            data={true}
            disabled={
              !(pricingType === ProductType.warrantiClassBased || pricingType === ProductType.warrantyTierBased)
            }
            class={ThemeButtonCircle}
            media={<Icon class="fas-download" />}
            render={lang.exportPrices}
          />
          <PriceImport
            productId={id}
            productType={pricingType}
            isDisabled={!(pricingType === ProductType.warrantiClassBased)}
            onSuccessImport={onSuccessImport}
          />
          <Button
            media={<Icon class="fas-save" />}
            render={lang.save}
            class={ThemeButtonCircle}
            active={isUpdated}
            disabled={!isUpdated || isSaving}
            htmlElementProps={{ title: lang.save }}
            onClick={saveAll}
          />
        </>
      }
    />
  );
}
